import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Alert, Box,
} from '@mui/material'
import { t } from '@lingui/macro'
import type { TestCountRow } from '@/core/api/statistics'
import { pivotTestsPerformed, type PivotedTestRow } from '../../lib/monthlyReportHelpers'

type Props = {
  data?: TestCountRow[]
  isLoading: boolean
  error?: unknown
  multiEstablishment: boolean
}

export function TestsPerformedTable({ data, isLoading, error, multiEstablishment }: Props) {
  if (isLoading) return <CircularProgress size={24} />
  if (error) return <Alert severity="error">{t`Error loading tests data`}</Alert>
  if (!data || data.length === 0) return <Typography color="text.secondary">{t`No data available`}</Typography>

  const { mesureRows, medicalActRows, establishments } = pivotTestsPerformed(data)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {mesureRows.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>{t`Measurements`}</Typography>
          <TestSubTable rows={mesureRows} establishments={establishments} multiEstablishment={multiEstablishment} />
        </Box>
      )}
      {medicalActRows.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>{t`Medical acts`}</Typography>
          <TestSubTable rows={medicalActRows} establishments={establishments} multiEstablishment={multiEstablishment} />
        </Box>
      )}
    </Box>
  )
}

type SubTableProps = {
  rows: PivotedTestRow[]
  establishments: string[]
  multiEstablishment: boolean
}

function TestSubTable({ rows, establishments, multiEstablishment }: SubTableProps) {
  if (!multiEstablishment) {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t`Exam`}</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>{t`Count`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.testLabel} hover>
                <TableCell sx={{ fontWeight: 500 }}>{row.testLabel}</TableCell>
                <TableCell align="center">{row.total}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const columnTotals: Record<string, number> = {}
  for (const row of rows) {
    for (const est of establishments) {
      columnTotals[est] = (columnTotals[est] || 0) + (row.counts[est] || 0)
    }
  }
  const grandTotal = rows.reduce((sum, r) => sum + r.total, 0)

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'primary.main' }}>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t`Exam`}</TableCell>
            {establishments.map(est => (
              <TableCell key={est} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                {est}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>{t`Total`}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.testLabel} hover>
              <TableCell sx={{ fontWeight: 500 }}>{row.testLabel}</TableCell>
              {establishments.map(est => (
                <TableCell key={est} align="center">{row.counts[est] || 0}</TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>{row.total}</TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ backgroundColor: 'action.hover' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>{t`Total`}</TableCell>
            {establishments.map(est => (
              <TableCell key={est} align="center" sx={{ fontWeight: 'bold' }}>{columnTotals[est] || 0}</TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>{grandTotal}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}
